import { ChevronDownIcon } from '@heroicons/react/16/solid'
import { cn } from '../../helpers/classnames'

export interface JosefSelectOption {
  value: string
  label: string
  description?: string
  disabled?: boolean
}

export interface JosefSelectGroup {
  label: string
  options: JosefSelectOption[]
}

export type JosefSelectSize = 'sm' | 'md' | 'lg'
export type JosefSelectVariant = 'default' | 'borderless' | 'ghost' | 'primary'

export interface JosefSelectProps {
  options?: JosefSelectOption[]
  groups?: JosefSelectGroup[]
  value?: string
  onChange?: (value: string) => void
  onValueChange?: (value: string) => void
  placeholder?: string
  label?: string
  help?: React.ReactNode
  className?: string
  triggerClassName?: string
  selectClassName?: string
  disabled?: boolean
  size?: JosefSelectSize
  variant?: JosefSelectVariant
}

const sizeStyles: Record<
  JosefSelectSize,
  {
    height: string
    padding: string
    text: string
    iconTop: string
    iconSize: string
  }
> = {
  sm: {
    height: 'h-8',
    padding: 'pl-2 pr-8 py-1',
    text: 'text-xs',
    iconTop: 'top-2',
    iconSize: 'h-3 w-3',
  },
  md: {
    height: 'h-10',
    padding: 'pl-3 pr-10 py-2',
    text: 'text-sm',
    iconTop: 'top-3',
    iconSize: 'h-4 w-4',
  },
  lg: {
    height: 'h-12',
    padding: 'pl-4 pr-12 py-3',
    text: 'text-base',
    iconTop: 'top-4',
    iconSize: 'h-4 w-4',
  },
}

export function JosefSelect({
  options = [],
  groups,
  value,
  onChange,
  onValueChange,
  placeholder = 'Select an option...',
  label,
  help,
  className = '',
  triggerClassName = '',
  selectClassName = '',
  disabled = false,
  size = 'md',
  variant = 'borderless',
}: JosefSelectProps) {
  const handleChange = (newValue: string) => {
    onChange?.(newValue)
    onValueChange?.(newValue)
  }

  const styles = sizeStyles[size]

  const variantStyles = {
    default:
      'border border-black/20 dark:border-white/20 focus:ring-2 focus:ring-black/20 dark:focus:ring-white/20 focus:border-black/40 dark:focus:border-white/40 hover:border-black/30 dark:hover:border-white/30',
    borderless: 'border-transparent focus:ring-0 focus:border-transparent',
    ghost:
      'bg-transparent dark:bg-transparent ring-black/10 dark:ring-white/10 text-black dark:text-white placeholder:text-black/60 dark:placeholder:text-white/60 hover:bg-black/5 dark:hover:bg-white/5 focus:border-black/20 dark:focus:border-white/20 focus:ring-black/10 dark:focus:ring-white/10 ring-1',
    primary:
      'bg-transparent dark:bg-transparent border-transparent text-black dark:text-white font-medium hover:bg-black/5 dark:hover:bg-white/5 focus:ring-0 focus:border-transparent',
  }

  return (
    <div className={cn('space-y-1.5', className)}>
      {label && (
        <label className="block text-sm font-medium text-black dark:text-white">
          {label}
        </label>
      )}
      <div className="relative">
        <select
        value={value || ''}
        onChange={(e) => handleChange(e.target.value)}
        disabled={disabled}
        className={cn(
          'w-full bg-white dark:bg-black rounded-lg text-black dark:text-white transition-colors appearance-none',
          styles.height,
          styles.padding,
          styles.text,
          'focus:outline-none',
          variantStyles[variant],
          disabled && 'opacity-50 cursor-not-allowed',
          triggerClassName,
          selectClassName
        )}
      >
        {placeholder && !options.some((o) => o.value === '') && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {groups
          ? groups.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.options.map((option) => (
                  <option
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                  >
                    {option.label}
                  </option>
                ))}
              </optgroup>
            ))
          : options.map((option) => (
              <option
                key={option.value}
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </option>
            ))}
      </select>
      <ChevronDownIcon
        className={cn(
          'absolute right-2 text-gray-500 dark:text-gray-400 pointer-events-none',
          styles.iconTop,
          styles.iconSize
        )}
      />
      </div>
      {help && (
        <p className="text-xs text-black/60 dark:text-white/60">{help}</p>
      )}
    </div>
  )
}

export default JosefSelect
